import { Divider, Stack } from '@mui/material';

import { ParityBarInline } from '@/ui/components/parity-bar/ParityBarInline';
import { ScoreTag } from '@/ui/components/score/ScoreTag';
import { GaugeScore } from '@/ui/components/score-bars/GaugeScore';
import { HorizontalScoreBar } from '@/ui/components/score-bars/HorizontalScoreBar';
import { VerticalScoreBar } from '@/ui/components/score-bars/VerticalScoreBar';
import { ScoreText } from '@/ui/components/score-text/ScoreText';
import { CustomAvgCapacityTag } from '@/ui/pages/app/project/venue/dates/view-list/components/CustomAvgCapacityTag';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { getColorForScore } from '@/ui/utils/get-color-for-score';

export function DevUIScoreComponents() {
  return (
    <>
      <Stack spacing={2} divider={<Divider />}>
        <ScoreTags />
        <CustomAvgCapacityTags />
        <ScoreTexts />
        <ParityBarInlines />
        <ScoreBars />
        <SemiCircleScores />
      </Stack>
    </>
  );
}

function ScoreTags() {
  return (
    <Stack sx={{ gap: 1 }}>
      ScoreTag horizontal (default)
      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        <ScoreTag value={'8'} colors={getColorForScore('A')} topic="co2" />
        <ScoreTag value={'34'} colors={getColorForScore('B')} topic="co2" />
        <ScoreTag value={'A'} colors={getColorForScore('A')} topic="co2" />
        <ScoreTag value={'A'} colors={getColorForScore('A')} topic="ess" />
        <ScoreTag value={'B'} colors={getColorForScore('B')} topic="ess" />
        <ScoreTag value={'C'} colors={getColorForScore('C')} topic="ess" />
        <ScoreTag value={'D'} colors={getColorForScore('D')} topic="env" />
        <ScoreTag value={'E'} colors={getColorForScore('E')} topic="env" />
        <ScoreTag value={'F'} colors={getColorForScore('F')} topic="env" />
        <ScoreTag value={''} colors={getColorForScore('')} topic="env" />
        <ScoreTag value={82.4} colors={getColorForScore('A')} topic="co2" />
      </Stack>
      ScoreTag vertical
      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        <ScoreTag
          value={'8'}
          colors={getColorForScore('A')}
          topic="co2"
          orientation="vertical"
        />
        <ScoreTag
          value={'A'}
          colors={getColorForScore('A')}
          topic="env"
          orientation="vertical"
        />
        <ScoreTag
          value={'C'}
          colors={getColorForScore('C')}
          topic="ess"
          orientation="vertical"
        />
      </Stack>
      ScoreTag clickable (onClick)
      <Stack sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <ScoreTag
          value={'8'}
          colors={getColorForScore('A')}
          topic="co2"
          onClick={() => alert('co2 clicked')}
        />
        <ScoreTag
          value={1200}
          colors={getColorForScore('')}
          topic="persons"
          onClick={() => alert('persons clicked')}
        />
        <ScoreTag
          value={'A'}
          colors={getColorForScore('A')}
          topic="ess"
          orientation="vertical"
          onClick={() => alert('ess vertical clicked')}
        />
      </Stack>
    </Stack>
  );
}

function CustomAvgCapacityTags() {
  return (
    <Stack sx={{ gap: 1 }}>
      CustomAvgCapacityTag
      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        <CustomAvgCapacityTag avgCapacity={500} />
        <CustomAvgCapacityTag
          avgCapacity={1200}
          onClick={() => alert('clicked')}
        />
        <CustomAvgCapacityTag
          avgCapacity={800}
          onClick={() => alert('clicked')}
          disabled
        />
      </Stack>
    </Stack>
  );
}

function ScoreTexts() {
  return (
    <Stack sx={{ flexDirection: 'row', gap: 4 }}>
      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        ScoreText default
        <ScoreText value={'8'} text={'Dates'} />
        <ScoreText value={'1092 km'} text={'Parcours'} />
      </Stack>
      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        ScoreText box
        <ScoreText value={'8'} text={'Dates'} variant="box" />
        <ScoreText value={'1092 km'} text={'Parcours'} variant="box" />
      </Stack>
      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        ScoreText no text
        <ScoreText value={'8'} />
        <ScoreText value={'1092 km'} variant="box" />
      </Stack>
    </Stack>
  );
}

function ScoreBars() {
  return (
    <Stack spacing={2}>
      VerticalScoreBar : lower / normal / higher
      <Stack sx={{ flexDirection: 'row', gap: 2 }}>
        <VerticalScoreBar
          value={10}
          colors={{
            main: ColorsRaw.scores.Anthracite_4,
            secondary: ColorsRaw.scores.Antracite_4012,
            text: ColorsRaw.basic.Cream,
          }}
          position="lower"
        />
        <VerticalScoreBar
          value={10}
          colors={{
            main: ColorsRaw.scores.Anthracite_4,
            secondary: ColorsRaw.scores.Antracite_4012,
            text: ColorsRaw.basic.Cream,
          }}
          position="normal"
        />
        <VerticalScoreBar
          value={10}
          colors={{
            main: ColorsRaw.scores.Anthracite_4,
            secondary: ColorsRaw.scores.Antracite_4012,
            text: ColorsRaw.basic.Cream,
          }}
          position="higher"
        />
      </Stack>
      HorizontalScoreBar : lower / normal / higher
      <Stack sx={{ flexDirection: 'column', gap: 2, width: '20rem' }}>
        <HorizontalScoreBar
          value={5}
          color="red"
          backColor="pink"
          position="lower"
        />
        <HorizontalScoreBar
          value={5}
          color="red"
          backColor="pink"
          position="normal"
        />
        <HorizontalScoreBar
          value={5}
          color="red"
          backColor="pink"
          position="higher"
        />
      </Stack>
    </Stack>
  );
}

function SemiCircleScores() {
  return (
    <Stack sx={{ flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
      <GaugeScore score={0} maxScore={0} />
      <GaugeScore score={2} maxScore={49} />
      <GaugeScore score={39} maxScore={70} />
      <GaugeScore score={10} maxScore={38} />
      <GaugeScore score={51} maxScore={65} />
      <GaugeScore score={669} maxScore={669} />
    </Stack>
  );
}

function ParityBarInlines() {
  return (
    <Stack sx={{ gap: 2 }}>
      ParityBarIline
      <ParityBarInline women={30} men={15} other={5} />
      <ParityBarInline women={15} men={15} other={0} />
      <ParityBarInline women={15} men={0} other={6} />
    </Stack>
  );
}
